import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, X, Pencil, Trash2, AlertTriangle } from 'lucide-react';

import { tasksApi } from '../../../api/tasks.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { t } from '../../../lib/i18n.js';

import { TaskFormModal } from './TaskFormModal.jsx';

const KIND_LABEL = { todo: 'Attività', alert: 'Alert', follow_up: 'Follow-up', logistics: 'Logistica', training: 'Formazione' };
const KIND_TONE = { todo: 'neutral', alert: 'warning', follow_up: 'info', logistics: 'neutral', training: 'neutral' };
const STATUS_LABEL = { open: 'Aperta', in_progress: 'In corso', done: 'Completata', dismissed: 'Ignorata' };

function isOverdue(task) {
  return task.dueAt && ['open', 'in_progress'].includes(task.status) && new Date(task.dueAt) <= new Date();
}

export function TasksPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canCreate = hasPermission('tasks:create');
  const canUpdate = hasPermission('tasks:update');
  const canDelete = hasPermission('tasks:delete');

  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['tasks', 'list'],
    fetcher: tasksApi.list,
    initialFilters: { open: true }
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tasks'] });
  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => tasksApi.update(id, { status }),
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message || t('common.error'))
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => tasksApi.remove(id),
    onSuccess: () => { toast.success('Attività eliminata.'); invalidate(); setConfirmTarget(null); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Attività"
        description="Attività operative, alert e follow-up. Ciò che richiede attenzione, in un unico posto."
        actions={canCreate ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuova attività</Button> : null}
      />

      <div className="flex flex-wrap items-end gap-3">
        <Select label="Stato" className="w-44" value={filters.status || ''} onChange={(e) => setFilters({ status: e.target.value || undefined, open: e.target.value ? undefined : true })}>
          <option value="">Aperte</option>
          <option value="open">Da fare</option>
          <option value="in_progress">In corso</option>
          <option value="done">Completate</option>
          <option value="dismissed">Ignorate</option>
        </Select>
        <Select label="Tipo" className="w-44" value={filters.kind || ''} onChange={(e) => setFilters({ kind: e.target.value || undefined })}>
          <option value="">Tutti</option>
          {Object.entries(KIND_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        <Select label="Priorità" className="w-40" value={filters.priority || ''} onChange={(e) => setFilters({ priority: e.target.value || undefined })}>
          <option value="">Tutte</option>
          <option value="high">Alta</option>
          <option value="normal">Normale</option>
          <option value="low">Bassa</option>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card><p className="text-sm text-ink-muted">Nessuna attività.</p></Card>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((task) => {
            const open = ['open', 'in_progress'].includes(task.status);
            return (
              <Card key={task._id} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={KIND_TONE[task.kind]}>{KIND_LABEL[task.kind] || task.kind}</Badge>
                    {task.priority === 'high' && <Badge tone="danger">Alta</Badge>}
                    {isOverdue(task) && <span className="inline-flex items-center gap-1 text-xs text-danger"><AlertTriangle className="h-3.5 w-3.5" />scaduta</span>}
                    {!open && <Badge tone="neutral">{STATUS_LABEL[task.status]}</Badge>}
                  </div>
                  <p className={`mt-1 text-sm ${open ? 'text-ink' : 'text-ink-muted line-through'}`}>{task.title}</p>
                  {task.description && <p className="text-xs text-ink-muted">{task.description}</p>}
                  <p className="mt-0.5 text-xs text-ink-faint">
                    {task.dueAt ? `Scadenza ${new Date(task.dueAt).toLocaleDateString('it-IT')}` : 'Nessuna scadenza'}
                    {task.assigneeId && ` - ${[task.assigneeId.firstName, task.assigneeId.lastName].filter(Boolean).join(' ')}`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {canUpdate && open && (
                    <>
                      <button type="button" title="Completa" className="rounded-md p-1.5 text-ink-muted hover:bg-success/10 hover:text-success" onClick={() => statusMutation.mutate({ id: task._id, status: 'done' })}>
                        <Check className="h-4 w-4" />
                      </button>
                      <button type="button" title="Ignora" className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle" onClick={() => statusMutation.mutate({ id: task._id, status: 'dismissed' })}>
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {canUpdate && !open && (
                    <button type="button" title="Riapri" className="text-xs text-ink-muted hover:text-ink" onClick={() => statusMutation.mutate({ id: task._id, status: 'open' })}>Riapri</button>
                  )}
                  {canUpdate && <button type="button" title="Modifica" className="rounded-md p-1.5 text-ink-muted hover:text-ink" onClick={() => { setEditing(task); setFormOpen(true); }}><Pencil className="h-4 w-4" /></button>}
                  {canDelete && <button type="button" title="Elimina" className="rounded-md p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger" onClick={() => setConfirmTarget(task)}><Trash2 className="h-4 w-4" /></button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="ghost" size="sm" disabled={!pagination.hasPrevPage} onClick={() => setPage(pagination.page - 1)}>Precedente</Button>
          <Button variant="ghost" size="sm" disabled={!pagination.hasNextPage} onClick={() => setPage(pagination.page + 1)}>Successiva</Button>
        </div>
      )}

      <TaskFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditing(null); }} initial={editing} />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare l'attività?"
        description={confirmTarget ? `"${confirmTarget.title}" sarà rimossa.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />
    </div>
  );
}
